import { Link } from "react-router-dom";
import { format } from "date-fns";
import { ArrowUpRight, Bell, FolderKanban, MessageCircleQuestion, Plus, StickyNote } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { useFetch } from "@/hooks/useFetch";
import { spaceParams } from "@/lib/spaces";
import { Empty, Loading, PageHeader, Panel, PrimaryButton } from "@/components/Bits";
import { ReminderItem } from "@/components/ReminderItem";
import { ProjectCard } from "@/components/ProjectCard";
import { NoteCard } from "@/components/NoteCard";
import { BriefingCard } from "@/components/ai/BriefingCard";
import { FunCorner } from "@/components/FunCorner";
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const useFun = () => {
  const [fun, setFun] = useState(null);
  const load = useCallback(() => api.get("/fun/state").then((r) => setFun(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  return [fun, load];
};

const greet = () => {
  const h = new Date().getHours();
  return h < 12 ? "Morning" : h < 18 ? "Afternoon" : "Evening";
};

const Stat = ({ label, value, icon: Icon, to, color, testid }) => (
  <Link to={to} data-testid={testid} className="group fade-up flex flex-col justify-between rounded-2xl border bg-card/70 p-5 transition-colors hover:border-white/20">
    <div className="flex items-center justify-between text-muted-foreground">
      <Icon className="h-4 w-4" style={{ color }} />
      <ArrowUpRight className="h-4 w-4 opacity-0 transition-opacity group-hover:opacity-100" />
    </div>
    <p className="mt-6 font-display text-4xl font-bold tracking-tight">{value ?? "–"}</p>
    <p className="mt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
  </Link>
);

const MoreLink = ({ to }) => <Link to={to} className="text-xs font-medium text-muted-foreground hover:text-foreground">View all</Link>;

export default function Dashboard() {
  const { user } = useAuth();
  const { space, openAdd } = useApp();
  const p = spaceParams(space);
  const reminders = useFetch("/reminders", { ...p, status: "pending" });
  const projects = useFetch("/projects", { ...p, status: "active" });
  const thoughts = useFetch("/notes", { ...p, kind: "thought", discussed: false });
  const notes = useFetch("/notes", { ...p, kind: "note" });
  const [fun, loadFun] = useFun();

  return (
    <div data-testid="dashboard-page">
      <PageHeader title={`${greet()}, ${user.name}.`} subtitle={format(new Date(), "EEEE d MMMM")}>
        <PrimaryButton onClick={() => openAdd("reminder")} data-testid="add-reminder-btn"><Plus className="h-4 w-4" /> Reminder</PrimaryButton>
      </PageHeader>
      <BriefingCard />
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Open reminders" value={reminders?.length} icon={Bell} to="/reminders" color="#3b82f6" testid="stat-reminders" />
        <Stat label="Active projects" value={projects?.length} icon={FolderKanban} to="/projects" color="#f59e0b" testid="stat-projects" />
        <Stat label="To discuss" value={thoughts?.length} icon={MessageCircleQuestion} to="/thoughts" color="#ec4899" testid="stat-thoughts" />
        <Stat label="Notes" value={notes?.length} icon={StickyNote} to="/notes" color="#10b981" testid="stat-notes" />
      </div>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-12">
        <Panel title="Up next" count={reminders?.length} action={<MoreLink to="/reminders" />} className="lg:col-span-7" testid="panel-reminders">
          {!reminders ? <Loading /> : reminders.length === 0 ? (
            <Empty icon={Bell} text="No open reminders. Nice." testid="empty-reminders" />
          ) : <div className="space-y-3">{reminders.slice(0, 6).map((r) => <ReminderItem key={r.id} r={r} />)}</div>}
        </Panel>
        <Panel title="To discuss" count={thoughts?.length} className="lg:col-span-5" testid="panel-thoughts"
          action={<button onClick={() => openAdd("thought")} data-testid="add-thought-btn" className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"><Plus className="h-3.5 w-3.5" /> Log thought</button>}>
          {!thoughts ? <Loading /> : thoughts.length === 0 ? (
            <Empty icon={MessageCircleQuestion} text="Nothing to bring up yet." testid="empty-thoughts" />
          ) : <div>{thoughts.slice(0, 5).map((n) => <NoteCard key={n.id} n={n} />)}</div>}
        </Panel>
        <Panel title="Active projects" count={projects?.length} action={<MoreLink to="/projects" />} className="lg:col-span-7" testid="panel-projects">
          {!projects ? <Loading /> : projects.length === 0 ? (
            <Empty icon={FolderKanban} text="No active projects." testid="empty-projects"
              action={<button onClick={() => openAdd("project")} data-testid="add-project-btn" className="text-sm font-semibold text-foreground underline-offset-4 hover:underline">Start a project</button>} />
          ) : <div className="grid gap-4 sm:grid-cols-2">{projects.slice(0, 4).map((pr) => <ProjectCard key={pr.id} p={pr} />)}</div>}
        </Panel>
        <Panel title="Recent notes" count={notes?.length} className="lg:col-span-5" testid="panel-notes"
          action={<button onClick={() => openAdd("note")} data-testid="add-note-btn" className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"><Plus className="h-3.5 w-3.5" /> Note</button>}>
          {!notes ? <Loading /> : notes.length === 0 ? (
            <Empty icon={StickyNote} text="No notes yet." testid="empty-notes" />
          ) : <div>{notes.slice(0, 4).map((n) => <NoteCard key={n.id} n={n} />)}</div>}
        </Panel>
        <div className="lg:col-span-12"><FunCorner fun={fun} reload={loadFun} /></div>
      </div>
    </div>
  );
}
